import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
    fetchPresentationVideos,
    savePresentationVideo,
    deletePresentationVideo,
    setOpenDialog,
    setSelectedVideo,
    setDeleteDialog,
    updateVideoFromStatus
} from '../slices/presentationSlice';
import type { BaseVideoFormData, ITraining } from '../../types';
import { useLanguage as useLanguageContext } from '../../i18n/LanguageContext';
import { getCookie } from '../../utils/cookieUtils';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../components/shared/NotificationSystem';

export const usePresentation = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { i18n, t } = useTranslation();
    const { language: contextLanguage } = useLanguageContext();
    const { showWarning } = useNotification();

    const {
        videos,
        selectedVideo,
        isLoading,
        error,
        deleteDialog,
        openDialog
    } = useSelector((state: RootState) => state.presentation);

    // Используем язык из контекста или куки вместо admin_language
    const language = contextLanguage || getCookie('language') || getCookie('i18next') || 'ru';

    const fetchVideos = async () => {
        try {
            // Синхронизируем язык i18n с текущим языком приложения
            if (i18n.language !== language) {
                i18n.changeLanguage(language);
            }
            await dispatch(fetchPresentationVideos(language));
        } catch (error) {
            console.error('Error fetching presentation videos:', error);
        }
    };

    const handleSubmit = async (data: BaseVideoFormData) => {
        try {
            const result = await dispatch(savePresentationVideo({
                data,
                language,
                videoFile: data.videoFile,
                thumbnailFile: data.thumbnailFile
            }));
            // Fetch updated videos after save
            await dispatch(fetchPresentationVideos(language));
            return result; // Возвращаем результат для отслеживания нового видео
        } catch (error) {
            console.error('Error saving presentation video:', error);
            throw error;
        }
    };

    const handleDelete = async (id: number) => {
        try {
            const video = videos.find(v => v.id === id);
            if (!video) return;

            if (video.language && video.language.toUpperCase() !== language.toUpperCase()) {
                showWarning(t('content.presentation.warnings.wrongLanguage', {
                    videoLang: video.language,
                    currentLang: language.toUpperCase()
                }));
                return;
            }

            dispatch(setDeleteDialog({
                open: true,
                videoId: id
            }));
        } catch (error) {
            console.error('Error setting up delete:', error);
        }
    };

    const confirmDelete = async () => {
        if (!deleteDialog.videoId) return;

        try {
            await dispatch(deletePresentationVideo(deleteDialog.videoId));
            // Fetch updated videos after delete
            await dispatch(fetchPresentationVideos(language));
            return true;
        } catch (error) {
            console.error('Error deleting video:', error);
            return false;
        }
    };

    const handleOpenDialog = (open: boolean) => {
        dispatch(setOpenDialog(open));
    };

    const handleVideoClick = (video: ITraining) => {
        dispatch(setSelectedVideo(video));
    };

    const handleCloseVideoPreview = () => {
        dispatch(setSelectedVideo(null));
    };

    const handleCancelDelete = () => {
        dispatch(setDeleteDialog({ open: false, videoId: null }));
    };

    const handleUpdateVideoFromStatus = (videoId: number, updates: Partial<ITraining>) => {
        dispatch(updateVideoFromStatus({ videoId, updates }));
    };

    return {
        videos,
        selectedVideo,
        isLoading,
        error,
        deleteDialog,
        openDialog,
        language,
        fetchVideos,
        handleSubmit,
        handleDelete,
        confirmDelete,
        handleOpenDialog,
        handleVideoClick,
        handleCloseVideoPreview,
        handleCancelDelete,
        updateVideoFromStatus: handleUpdateVideoFromStatus
    };
}; 